import type {
  AdmissionDTO,
  BookingDTO,
  BookingPaymentDTO,
  FulfillmentDTO,
} from '@/types/dto/agent';
import type { Voucher } from '@/types/purchase';

export const isTicketBooking = (booking?: BookingDTO | null): boolean => {
  if (!booking) return false;

  return Boolean(booking.bookedTrips?.length);
};

export const isTravelPassBooking = (booking?: BookingDTO | null): boolean => {
  if (!booking) return false;

  return Boolean(booking.nonTripOffers?.length);
};

export const getBookingAdmissions = (
  booking?: BookingDTO | null
): Array<AdmissionDTO> => {
  if (!booking?.bookedTrips?.length && !booking?.nonTripOffers?.length)
    return [];
  const { bookedTrips, nonTripOffers } = booking;

  return bookedTrips?.length
    ? bookedTrips.reduce<Array<AdmissionDTO>>(
        (admissions, trip) =>
          [
            ...admissions,
            ...(trip.bookedOffers?.reduce<Array<AdmissionDTO>>(
              (acc, { admissions }) =>
                admissions ? [...acc, ...admissions] : acc,
              []
            ) ?? []),
          ].filter(({ passengerIds }) => passengerIds),
        []
      )
    : (nonTripOffers?.reduce<Array<AdmissionDTO>>(
        (acc, { admissions }) => (admissions ? [...acc, ...admissions] : acc),
        []
      ) ?? []);
};

export const findBookingBookedTripReservationByReservationId = (
  booking: BookingDTO | null,
  reservationId?: string
) => {
  if (!reservationId) return undefined;

  return booking?.bookedTrips
    ?.flatMap((trip) => trip.bookedOffers || [])
    ?.flatMap((offer) => offer.admissions || [])
    ?.flatMap((admission) => admission.reservations || [])
    ?.find((reservation) => reservation.id === reservationId);
};

export const findBookingBookedTripFeesByFeeRefs = (
  booking: BookingDTO | null,
  feeRefs?: string[]
) => {
  if (!feeRefs || !booking?.bookedTrips) return undefined;

  return booking.bookedTrips
    .flatMap((trip) => trip.bookedOffers || [])
    .flatMap((offer) => offer.fees || [])
    .filter((fee) => fee.id && feeRefs.includes(fee.id));
};

export const mapBookingVoucherPaymentsToVouchers = (
  voucherPayments: Array<BookingPaymentDTO>
): Array<Voucher> =>
  voucherPayments
    .map(({ voucherCode, paidAmount }) => {
      if (!(voucherCode && paidAmount?.amount && paidAmount?.currency)) {
        return null;
      }

      return {
        voucherCode,
        amount: {
          amount: paidAmount.amount,
          currency: paidAmount.currency,
        },
      };
    })
    .filter((voucher): voucher is Voucher => !!voucher);

export const orderBookingTripsBasedOnDepartureTime = (
  booking: BookingDTO | null
): BookingDTO | null =>
  booking
    ? {
        ...booking,
        bookedTrips: (booking.bookedTrips ? [...booking.bookedTrips] : []).sort(
          (a, b) =>
            new Date(a.departureTime).getTime() -
            new Date(b.departureTime).getTime()
        ),
      }
    : null;

export const getBookingNonTripOfferFulfillments = (
  booking: BookingDTO | null
): Array<FulfillmentDTO> =>
  (booking?.nonTripOffers ?? []).flatMap((offer) =>
    (offer.admissions ?? []).flatMap(
      (admission) => admission.fulfillments ?? []
    )
  );

export const getBookingFulfillments = (
  booking: BookingDTO | null
): Array<{ id: string; isOutBound?: boolean }> => {
  const isTravelPass = isTravelPassBooking(booking);

  if (isTravelPass) {
    return (
      booking?.nonTripOffers?.flatMap(
        (offer) =>
          offer.admissions?.flatMap(
            (admission) =>
              admission.fulfillments
                ?.map((fulfillment) => fulfillment.id)
                .filter((id): id is string => id !== undefined)
                .map((id) => ({ id })) ?? []
          ) ?? []
      ) ?? []
    );
  }

  return (
    booking?.bookedTrips?.flatMap(
      (trip) =>
        trip.bookedOffers?.flatMap(
          (offer) =>
            offer.admissions?.flatMap(
              (admission) =>
                admission.fulfillments
                  ?.map((fulfillment) => fulfillment.id)
                  .filter((id): id is string => id !== undefined)
                  .map((id) => ({
                    id,
                    isOutBound: trip.isOutbound,
                  })) ?? []
            ) ?? []
        ) ?? []
    ) ?? []
  );
};
